/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { SearchResultsAnnouncer } from 'src/core/components/SearchResultsAnnouncer';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { FilterItemWithCheckbox } from 'src/verticals/utterancesFilters/components/FilterItemWithCheckbox';
import { FiltersItem, SelectionChange } from 'src/verticals/utterancesFilters/types/Filters';

export type FiltersSearchProps = {
    /**
     * The items used for filtration
     */
    items: FiltersItem[];

    /**
     * Handler function for item selection
     */
    onSelectionChange: (change: SelectionChange) => void;

    /**
     * A flag to show option for selecting all or not.
     */
    showSelectAll?: boolean;

    /**
     * A Callback fires when select all options is toggled.
     */
    onToggleSelectAll?: (isChecked: boolean) => void;
};

const messages = defineMessages({
    searchInput: {
        id: 'utterances.filtersSearch.searchInput',
        defaultMessage: 'Search'
    },
    all: {
        id: 'utterances.filtersSearch.all',
        defaultMessage: 'All'
    }
});

const { styleComponent } = getStylistV2('Filters');

const ItemsSection = styleComponent<IStackProps>(Stack)('ItemsSection', {
    marginLeft: 8
});

/**
 * Represents the filters search and filtration process
 */
export const FiltersSearch = (props: FiltersSearchProps) => {
    const { items, onSelectionChange, showSelectAll, onToggleSelectAll } = props;
    const { localize } = useLocalization();
    const [query, setQuery] = React.useState('');

    const filteredItems = items
        .filter(item => item.name.toLowerCase().includes(query.toLowerCase()))
        .sort((l, r) => (l.name.toLocaleLowerCase() > r.name.toLocaleLowerCase() ? 1 : -1))
        .map(item => <FilterItemWithCheckbox key={item.id} item={item} onSelectionChange={onSelectionChange} />);

    return (
        <Stack tokens={{ childrenGap: 15 }}>
            <LuisDebouncedSearchBox
                ariaLabel={localize(messages.searchInput)}
                placeholder={localize(messages.searchInput)}
                value={query}
                onChange={(_, newValue?: string) => setQuery(newValue)}
                debounceTime={0}
            />

            {query.length ? <SearchResultsAnnouncer itemsLength={filteredItems.length} /> : null}

            <ItemsSection tokens={{ childrenGap: 16 }}>
                {showSelectAll && (
                    <Checkbox
                        data-automation-id="selectAll"
                        label={localize(messages.all)}
                        onChange={(_, isChecked) => onToggleSelectAll(isChecked)}
                        checked={items.reduce((acc, item) => acc && item.isChecked, true)}
                    />
                )}

                {filteredItems}
            </ItemsSection>
        </Stack>
    );
};
